import { useCallback, useEffect, useRef } from 'react';
import { useUserSocket } from './useUserSocket';

/**
 * useRoleBuilderUpdates — listens to `role_builder_update` WebSocket events
 * scoped to the current user, coalesces them per-rolId in a 400ms window, and
 * dispatches to one of four callbacks based on the payload shape.
 *
 * Coalescence semantics:
 *   - Each `rolId` has its own pending entry. Late events for the same rolId
 *     within 400ms merge their `changes` (last value wins per key) and reset
 *     the timer. Fires once with the merged payload when the window closes.
 *   - Different rolIds within the same 400ms window are independent — they
 *     each fire on their own schedule. So a bulk of N create events leads to
 *     N callbacks, one per rolId, in the order they came in.
 *   - 400ms is wide enough to swallow N8N create→action-cards double-emits
 *     for the same rolId, and narrow enough not to fuse events for different
 *     rolIds in a bulk turn (LangChain agent v1 fires tools sequentially with
 *     ≥1s spacing).
 *
 * Dispatch rules (in priority order):
 *   - `changes.archived === true`  → onArchive(rolId, changes)
 *   - `changes.archived === false` → onRestore(rolId, changes)
 *   - rolId not present in `knownRolIds` → onInsert(rolId, changes, payload)
 *   - else → onUpdate(rolId, changes, payload)
 *
 * `knownRolIds` is read from a ref the caller updates externally so the
 * insert-vs-update decision always reflects the current grid state without
 * forcing a re-subscribe (the listener identity is stable).
 *
 * Mirror: src/context/RagJobContext.jsx:112-233 (listener pattern),
 *         src/components/editor/features/roles/RoleBuilderChat.jsx (debounce ref pattern).
 *
 * @param {object} options
 * @param {(rolId: string, changes: object, payload: object) => void} [options.onInsert]
 * @param {(rolId: string, changes: object, payload: object) => void} [options.onUpdate]
 * @param {(rolId: string, changes: object) => void} [options.onArchive]
 * @param {(rolId: string, changes: object) => void} [options.onRestore]
 * @param {React.MutableRefObject<Set<string>>} [options.knownRolIdsRef]
 *   Ref to a Set of rolIds currently rendered. Used to discriminate
 *   insert vs update for non-archive events. If omitted, all non-archive
 *   events go to onUpdate.
 */
export const COALESCE_MS = 400;

export const useRoleBuilderUpdates = ({
  onInsert,
  onUpdate,
  onArchive,
  onRestore,
  knownRolIdsRef,
} = {}) => {
  const { socket, isConnected } = useUserSocket();

  // rolId → { changes, timer, lastPayload }
  const pendingRef = useRef(new Map());

  // Keep callbacks in refs so the WS handler identity stays stable across
  // re-renders. Otherwise every parent re-render would tear down and re-add
  // the socket listener, which races with in-flight events.
  const handlersRef = useRef({ onInsert, onUpdate, onArchive, onRestore });
  useEffect(() => {
    handlersRef.current = { onInsert, onUpdate, onArchive, onRestore };
  }, [onInsert, onUpdate, onArchive, onRestore]);

  const dispatch = useCallback((rolId, mergedChanges, lastPayload) => {
    const { onInsert: i, onUpdate: u, onArchive: a, onRestore: r } = handlersRef.current;
    if (mergedChanges?.archived === true) {
      a?.(rolId, mergedChanges);
      return;
    }
    if (mergedChanges?.archived === false) {
      r?.(rolId, mergedChanges);
      return;
    }
    const known = knownRolIdsRef?.current;
    if (known && !known.has(rolId)) {
      i?.(rolId, mergedChanges, lastPayload);
    } else {
      u?.(rolId, mergedChanges, lastPayload);
    }
  }, [knownRolIdsRef]);

  useEffect(() => {
    if (!socket || !isConnected) return undefined;

    const handler = (data) => {
      if (!data || !data.rolId) return;
      const rolId = data.rolId;
      const incomingChanges = data.changes || {};
      const existing = pendingRef.current.get(rolId);

      // Hard rule: archive/restore are terminal and must NOT be coalesced
      // with non-archive updates that happen to land in the same window.
      // Cancel any pending non-archive update for this rolId and fire the
      // terminal event immediately on the trailing edge of its own timer.
      const isTerminal = incomingChanges.archived === true || incomingChanges.archived === false;
      if (isTerminal && existing && existing.changes.archived === undefined) {
        clearTimeout(existing.timer);
        pendingRef.current.delete(rolId);
      }

      const mergedChanges = {
        ...(existing?.changes || {}),
        ...incomingChanges,
      };
      if (existing?.timer) clearTimeout(existing.timer);

      const timer = setTimeout(() => {
        const entry = pendingRef.current.get(rolId);
        pendingRef.current.delete(rolId);
        if (entry) dispatch(rolId, entry.changes, entry.lastPayload);
      }, COALESCE_MS);

      pendingRef.current.set(rolId, {
        changes: mergedChanges,
        timer,
        lastPayload: data,
      });
    };

    socket.on('role_builder_update', handler);

    return () => {
      socket.off('role_builder_update', handler);
      // Flush nothing — clear pending timers so a late tick after unmount
      // doesn't fire callbacks against a stale tree.
      const pending = pendingRef.current;
      pending.forEach(({ timer }) => clearTimeout(timer));
      pending.clear();
    };
  }, [socket, isConnected, dispatch]);

  return { isConnected };
};

export default useRoleBuilderUpdates;
